import { type ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { removeOverlayHistoryMarker, useOverlayHistoryDismiss } from './useOverlayHistoryDismiss'
import { usePanToClose } from './usePanToClose'

const BOTTOM_SHEET_HISTORY_MARKER = 'qscBottomSheet'
const BOTTOM_SHEET_ANIMATION_MS = 180
const shouldSkipHistoryBack = (options: unknown) =>
  typeof options === 'object' &&
  options !== null &&
  'skipHistoryBack' in options &&
  Boolean((options as { skipHistoryBack?: boolean }).skipHistoryBack)

interface SheetOptions {
  title?: string
  content: ReactNode
  dismissOnBackdrop?: boolean
  onClose?: () => void
}

type Listener = (options: SheetOptions | null) => void
type SheetState = SheetOptions & { closing: boolean }

let currentSheet: SheetOptions | null = null
const listeners = new Set<Listener>()

function emit() {
  for (const listener of listeners) listener(currentSheet)
}

export function openBottomSheet(options: SheetOptions) {
  currentSheet = options
  emit()
}

export function closeBottomSheet(options?: unknown) {
  if (shouldSkipHistoryBack(options)) {
    removeOverlayHistoryMarker(BOTTOM_SHEET_HISTORY_MARKER)
  }
  const onClose = currentSheet?.onClose
  currentSheet = null
  emit()
  onClose?.()
}

export function BottomSheetHost() {
  const [options, setOptions] = useState<SheetState | null>(currentSheet ? { ...currentSheet, closing: false } : null)
  const closeTimerRef = useRef<number | null>(null)
  const dismiss = useOverlayHistoryDismiss(Boolean(options && !options.closing), closeBottomSheet, BOTTOM_SHEET_HISTORY_MARKER)
  const { dragY, dragHandleProps } = usePanToClose({ onClose: dismiss })

  useEffect(() => {
    const clearCloseTimer = () => {
      if (closeTimerRef.current === null) return
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    const handleSheetChange = (nextOptions: SheetOptions | null) => {
      clearCloseTimer()

      if (nextOptions) {
        setOptions({ ...nextOptions, closing: false })
        return
      }

      setOptions((current) => (current ? { ...current, closing: true } : null))
      closeTimerRef.current = window.setTimeout(() => {
        setOptions(null)
        closeTimerRef.current = null
      }, BOTTOM_SHEET_ANIMATION_MS)
    }

    listeners.add(handleSheetChange)
    return () => {
      clearCloseTimer()
      listeners.delete(handleSheetChange)
    }
  }, [])

  useEffect(() => {
    if (!options) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [dismiss, options])

  if (!options || typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-end justify-center lg:items-center">
      <button
        type="button"
        tabIndex={-1}
        aria-label="하단 시트 닫기"
        className={`absolute inset-0 bg-black/40 ${options.closing ? 'qsc-bottom-sheet-backdrop-out' : 'qsc-bottom-sheet-backdrop-in'}`}
        onClick={() => {
          if (options.dismissOnBackdrop !== false) dismiss()
        }}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label={options.title ?? '하단 시트'}
        className={`relative max-h-[calc(100dvh-var(--safe-top)-16px)] w-full overflow-hidden rounded-t-xl bg-surface shadow-sheet transition-[max-height,height,transform] duration-slow ease-out lg:max-w-lg lg:rounded-lg lg:shadow-overlay ${
          options.closing ? 'qsc-bottom-sheet-out' : 'qsc-bottom-sheet-in'
        }`}
        style={dragY > 0 ? { transform: `translateY(${dragY}px)` } : undefined}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div
          {...dragHandleProps}
          className="flex touch-none cursor-grab justify-center px-4 pb-1 pt-2.5 active:cursor-grabbing sm:px-5 sm:pt-3"
          aria-hidden="true"
        >
          <span className="h-1 w-10 rounded-full bg-border-subtle" />
        </div>
        <div
          {...dragHandleProps}
          className="flex touch-none cursor-grab items-center justify-between gap-3 border-b border-border-subtle px-4 pb-3 pt-1.5 active:cursor-grabbing sm:gap-4 sm:px-5 sm:pb-4 sm:pt-2"
        >
          <h2 className="text-preset-heading text-text">{options.title ?? ''}</h2>
          <button
            type="button"
            className="flex size-8 items-center justify-center rounded-full text-text-subtle hover:bg-gray-100"
            onClick={dismiss}
            aria-label="닫기"
          >
            <X aria-hidden className="size-4" />
          </button>
        </div>
        <div className="max-h-[calc(100dvh-var(--safe-top)-88px)] overflow-auto px-4 py-3 pb-[calc(var(--safe-bottom)+14px)] sm:max-h-[calc(100dvh-var(--safe-top)-96px)] sm:px-5 sm:py-4 sm:pb-[calc(var(--safe-bottom)+16px)]">
          {options.content}
        </div>
      </section>
    </div>,
    document.body,
  )
}
